import { AdPlacement } from 'types/adTypes';

/**
 * The size the editor draws a variant at, per placement.
 *
 * ⭐ SYM-6006 ADDS `portraitFeed`, AND IT IS THE WHOLE TICKET.
 *
 * Feed has always been 1000x1000 here, because the only artwork feed ever had was the square
 * overlays. The v2 templates render 1080x1350 (FrameSpec::feed on the API), so for a customer who
 * has them the editor was framing a square while a portrait ad went out the door.
 *
 * ⚠️ THE SWITCH IS PER CUSTOMER, NOT PER AD, and that is not a new decision — it follows from how
 * the feature has worked since SYM-5792. The capability is resolved for one customer at a time; a
 * customer who has it is shown only the v2 templates (the legacy section is not rendered for them
 * at all, PlacementMenu.tsx); and every v2 template is portrait. So their frame is portrait. A
 * customer without the capability keeps 1000x1000 and notices nothing, which is the no-regression
 * guarantee SYM-5792 made.
 *
 * Per-AD switching was considered and rejected: the shape would change while somebody is working,
 * and fabric does not re-lay-out what is already on the canvas when its dimensions change.
 *
 * ⚠️ Everything downstream follows these two numbers rather than repeating them — the crop area
 * the customer drags (Sidebar.tsx passes `aspect={canvasWidth / canvasHeight}`), the default
 * cover-crop (AdEditorContext's getDefaultCoverCrop), and the fabric canvas itself. So this is the
 * one place the shape is decided.
 */
export const getCanvasHeight = (
  placement?: AdPlacement,
  portraitFeed = false
): number => {
  switch (placement) {
    case AdPlacement.FEED:
      return portraitFeed ? 1350 : 1000;
    case AdPlacement.STORIES:
    case AdPlacement.REELS:
      return 1920;
    default:
      return 1;
  }
};

export const getCanvasWidth = (
  placement?: AdPlacement,
  portraitFeed = false
): number => {
  switch (placement) {
    case AdPlacement.FEED:
      return portraitFeed ? 1080 : 1000;
    case AdPlacement.STORIES:
    case AdPlacement.REELS:
      return 1080;
    default:
      return 1;
  }
};

/**
 * SYM-6006. The aspect the editor FRAME is drawn at, as a Tailwind-friendly ratio string.
 *
 * Kept next to the sizes above so the visible frame and the canvas underneath it cannot disagree —
 * they were separate before, which is how the frame could be square while the render was portrait.
 */
export const getCanvasAspect = (
  placement?: AdPlacement,
  portraitFeed = false
): string | undefined => {
  switch (placement) {
    case AdPlacement.FEED:
      return portraitFeed ? '4 / 5' : '1 / 1';
    case AdPlacement.STORIES:
    case AdPlacement.REELS:
      return '9 / 16';
    default:
      return undefined;
  }
};

/**
 * The size a piece of artwork declares for itself, or null when it declares none.
 *
 * SYM-6010. Read by regex on the opening tag rather than by parsing, on purpose: this runs for
 * every tile in the Overlays panel, and custom artwork can carry embedded images as base64, so
 * handing the whole string to a DOM parser to learn two numbers is wasteful. The rule matches
 * sympl-feed-port-assets/custom-overlay-probe.php exactly -- explicit width/height first, view box
 * as the fallback -- so the panel and the probe can never disagree about what a file declares.
 */
export const readDeclaredSvgSize = (
  svg?: string | null
): { width: number; height: number } | null => {
  if (!svg) return null;

  const w = svg.match(/<svg[^>]*\swidth="([\d.]+)/i)?.[1];
  const h = svg.match(/<svg[^>]*\sheight="([\d.]+)/i)?.[1];

  let width = w ? Number(w) : null;
  let height = h ? Number(h) : null;

  if (width === null || height === null) {
    const box = svg.match(/viewBox="[\d.-]+\s+[\d.-]+\s+([\d.]+)\s+([\d.]+)"/i);
    if (box) {
      width = width ?? Number(box[1]);
      height = height ?? Number(box[2]);
    }
  }

  if (!width || !height || width <= 0 || height <= 0) return null;

  return { width, height };
};

/**
 * Does this artwork belong in a frame of this size, and if so at what scale?
 *
 * SYM-6010. Nothing in the compositing path scales an overlay -- each shape is placed at the
 * position written in the file (AdEditorContext's setCanvasTo). So artwork only lands correctly if
 * it was drawn at exactly the frame size, and a square drawing in the portrait frame does not
 * stretch or letterbox: it sits in the top-left and leaves the bottom quarter of the ad bare.
 *
 * 'scale' is deliberately limited to artwork that is ALREADY the right shape. SYM-6010's original
 * write-up rejected scaling, and it was right about the case it had in mind -- forcing a square
 * into a portrait. Resizing a 4:5 drawing to a 4:5 frame is a different operation with none of the
 * distortion, and it is what keeps a designer's double-size export working.
 *
 * The tolerance is the same 0.02 the probe uses, which is loose enough for a file that rounds its
 * numbers and far tighter than the gap between any two frames we draw.
 */
export type OverlayFit =
  | { kind: 'exact' }
  | { kind: 'scale'; factor: number }
  | { kind: 'mismatch' }
  | { kind: 'undeclared' };

export const getOverlayFit = (
  svg: string | null | undefined,
  canvasWidth: number,
  canvasHeight: number
): OverlayFit => {
  const declared = readDeclaredSvgSize(svg);
  if (!declared) return { kind: 'undeclared' };
  if (canvasWidth <= 0 || canvasHeight <= 0) return { kind: 'undeclared' };

  if (
    Math.round(declared.width) === canvasWidth &&
    Math.round(declared.height) === canvasHeight
  ) {
    return { kind: 'exact' };
  }

  const sameShape =
    Math.abs(declared.width / declared.height - canvasWidth / canvasHeight) <
    0.02;

  if (!sameShape) return { kind: 'mismatch' };

  return { kind: 'scale', factor: canvasWidth / declared.width };
};

/**
 * Should this artwork be OFFERED to this customer?
 *
 * SYM-6010. Split from getOverlayFit() because the two halves have deliberately different reach.
 * Sizing runs for everybody -- it is a no-op for artwork that already matches its frame, which is
 * every piece of artwork in production today. HIDING runs only for a customer on the v2 templates,
 * whose frame just changed under them. A customer who never opted in keeps seeing exactly what
 * they saw yesterday, which is the no-regression guarantee SYM-5792 made.
 *
 * Artwork that declares no size counts as not fitting: we cannot tell whether it belongs, and the
 * cost of guessing wrong is a live campaign with a bare strip down the bottom, against one support
 * conversation for guessing the other way. No such artwork exists today.
 */
export const overlayIsOfferable = (
  fit: OverlayFit,
  customerHasTemplatesV2: boolean
): boolean => {
  if (!customerHasTemplatesV2) return true;
  return fit.kind === 'exact' || fit.kind === 'scale';
};
